import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const LEVEL_TONE = {
  "élevé": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "moyen": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "faible": "bg-zinc-600/30 text-zinc-400 border-zinc-600/40",
};

export default function AdmissionCandidatesModule() {
  const [data, setData] = useState({ candidates: [], counts: {}, statuts: [] });
  const [loading, setLoading] = useState(true);
  const [niveau, setNiveau] = useState("");
  const [open, setOpen] = useState(null);
  const [settings, setSettings] = useState(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const publicLink = `${window.location.origin}/admission`;
  const qrSrc = `${BACKEND}/api/admission/qr.png`;

  useEffect(() => { api.get("/admission/settings").then(({ data }) => setSettings(data)).catch(() => {}); }, []);

  const saveSettings = async (body) => {
    const next = { ...settings, ...body };
    setSettings(next);
    try { const { data } = await api.put("/admission/settings", next); setSettings(data); toast.success("Réglages enregistrés"); }
    catch { toast.error("Erreur"); }
  };
  const copyLink = async () => { try { await navigator.clipboard.writeText(publicLink); toast.success("Lien copié !"); } catch { toast.error("Copie impossible"); } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admission/candidates${niveau ? `?niveau=${niveau}` : ""}`);
      setData(data);
    } catch { toast.error("Erreur de chargement"); } finally { setLoading(false); }
  }, [niveau]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try { await api.patch(`/admission/candidates/${id}`, body); toast.success("Mis à jour"); load(); }
    catch { toast.error("Erreur"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette fiche ?")) return;
    try { await api.delete(`/admission/candidates/${id}`); toast.success("Supprimé"); setOpen(null); load(); }
    catch { toast.error("Erreur"); }
  };

  const c = data.counts || {};

  return (
    <div className="p-6 space-y-6" data-testid="admission-candidates-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-zinc-100">🎯 Admissions — Candidats qualifiés</h1>
          <p className="text-sm text-zinc-400 mt-1">Fiches du parcours public <code className="text-amber-400">/admission</code> — scorées automatiquement.</p>
        </div>
        <a href="/admission" target="_blank" rel="noreferrer" data-testid="adm-open-public"
          className="text-sm px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-medium">↗ Ouvrir le formulaire public</a>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Partage : lien + QR */}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="adm-share-card">
          <h3 className="font-bold text-zinc-100 mb-1">📣 Partager le formulaire</h3>
          <p className="text-xs text-zinc-500 mb-3">Mets ce lien dans tes pubs Facebook/Instagram et le QR sur tes affiches dojo.</p>
          <div className="flex gap-2 mb-4">
            <input readOnly value={publicLink} data-testid="adm-public-link"
              className="flex-1 rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-sm text-zinc-300" />
            <button onClick={copyLink} data-testid="adm-copy-link" className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium">Copier</button>
          </div>
          <div className="flex items-center gap-4">
            <img src={qrSrc} alt="QR code admission" data-testid="adm-qr-img"
              className="w-32 h-32 rounded-lg bg-white p-1.5 border border-white/10" />
            <div className="text-xs text-zinc-400">
              <p className="mb-2">QR vers <b className="text-amber-300">/admission</b> (pointe vers ton site en production).</p>
              <a href={qrSrc} download="qr-admission-kaizen.png" data-testid="adm-qr-download"
                className="inline-block px-3 py-1.5 rounded-lg border border-white/15 hover:border-amber-500 text-zinc-200">⬇ Télécharger le QR</a>
            </div>
          </div>
        </div>

        {/* Réglages notifications */}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="adm-settings-card">
          <h3 className="font-bold text-zinc-100 mb-1">🔔 Notifications équipe</h3>
          <p className="text-xs text-zinc-500 mb-3">Sois alerté quand un bon candidat soumet une fiche. Tout est désactivable ici.</p>
          {!settings ? <p className="text-sm text-zinc-500">Chargement…</p> : (
            <div className="space-y-3">
              {[
                ["notify_email_enabled", "Alerte par courriel (Resend)"],
                ["notify_sms_enabled", "Alerte par SMS (Twilio)"],
                ["push_to_crm_enabled", "Remonter dans le CRM Leads"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center justify-between cursor-pointer" data-testid={`adm-toggle-${key}`}>
                  <span className="text-sm text-zinc-200">{label}</span>
                  <button type="button" onClick={() => saveSettings({ [key]: !settings[key] })}
                    className={`w-11 h-6 rounded-full transition-colors relative ${settings[key] ? "bg-emerald-500" : "bg-zinc-700"}`}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${settings[key] ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </label>
              ))}
              <div className="grid sm:grid-cols-2 gap-2 pt-1">
                <input placeholder="Courriel d'alerte" defaultValue={settings.notify_email} data-testid="adm-notify-email"
                  onBlur={(e) => e.target.value !== settings.notify_email && saveSettings({ notify_email: e.target.value })}
                  className="rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-sm text-zinc-200" />
                <input placeholder="Téléphone SMS (+1…)" defaultValue={settings.notify_phone} data-testid="adm-notify-phone"
                  onBlur={(e) => e.target.value !== settings.notify_phone && saveSettings({ notify_phone: e.target.value })}
                  className="rounded-lg bg-zinc-900 border border-white/10 px-3 py-2 text-sm text-zinc-200" />
              </div>
              <label className="flex items-center justify-between text-sm">
                <span className="text-zinc-400">Déclencher à partir du niveau</span>
                <select value={settings.notify_min_level} onChange={(e) => saveSettings({ notify_min_level: e.target.value })}
                  data-testid="adm-min-level" className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-zinc-200 [&>option]:bg-zinc-900">
                  <option value="élevé">Élevé seulement</option>
                  <option value="moyen">Moyen et +</option>
                </select>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* ENTONNOIR DE CONVERSION */}
      <div className="rounded-xl border border-white/10 bg-gradient-to-br from-amber-500/5 to-red-500/5 p-5" data-testid="adm-funnel">
        <h3 className="font-bold text-zinc-100 mb-3">📊 Conversion des candidatures</h3>
        <div className="flex items-stretch gap-2 flex-wrap">
          {[
            ["Fiches reçues", data.funnel?.recues ?? 0, ""],
            ["Qualifiés « élevé »", `${data.funnel?.eleve ?? 0}`, `${data.funnel?.pct_eleve ?? 0}% des fiches`],
            ["Rappelés / traités", data.funnel?.rappeles ?? 0, ""],
            ["Inscrits", data.funnel?.inscrits ?? 0, `${data.funnel?.pct_inscrits ?? 0}% de conversion`],
          ].map(([lbl, val, sub], i) => (
            <div key={lbl} className="flex items-center gap-2 flex-1 min-w-[130px]">
              <div className="flex-1 rounded-lg bg-zinc-900/60 border border-white/10 px-3 py-3 text-center">
                <div className="text-2xl font-black text-zinc-100">{val}</div>
                <div className="text-[11px] text-zinc-400 uppercase tracking-wide mt-0.5">{lbl}</div>
                {sub && <div className="text-[11px] text-amber-400 mt-0.5">{sub}</div>}
              </div>
              {i < 3 && <span className="text-zinc-600 text-lg">→</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Total", c.total, ""], ["Élevé", c.eleve, "élevé"], ["Moyen", c.moyen, "moyen"], ["Faible", c.faible, "faible"]].map(([lbl, val, lv]) => (
          <button key={lbl} onClick={() => setNiveau(niveau === lv ? "" : lv)} data-testid={`adm-filter-${lbl.toLowerCase()}`}
            className={`text-left rounded-xl border p-4 transition-all ${niveau === lv && lv ? "border-amber-500 bg-amber-500/5" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
            <div className="text-2xl font-black text-zinc-100">{val ?? 0}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest mt-1">{lbl}</div>
          </button>
        ))}
      </div>

      {loading ? <p className="text-sm text-zinc-400">Chargement…</p> : data.candidates.length === 0 ? (
        <div className="text-center py-16 text-zinc-500" data-testid="adm-empty">Aucune fiche pour l'instant. Partage le lien <code className="text-amber-400">/admission</code> dans tes pubs.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-zinc-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Candidat</th><th className="text-left px-3 py-2">Programme</th>
                <th className="text-left px-3 py-2">Score</th><th className="text-left px-3 py-2">Niveau</th>
                <th className="text-left px-3 py-2">Statut</th><th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.candidates.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`adm-row-${r.id}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-zinc-100">{r.nom || "—"}</div>
                    <div className="text-xs text-zinc-500">{r.courriel} · {r.telephone}</div>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-300">{r.programme_recommande}</td>
                  <td className="px-3 py-2.5 font-bold text-zinc-100">{r.score}</td>
                  <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full border ${LEVEL_TONE[r.niveau_qualification] || ""}`}>{r.niveau_qualification}</span></td>
                  <td className="px-3 py-2.5">
                    <select value={r.statut} onChange={(e) => patch(r.id, { statut: e.target.value })} data-testid={`adm-statut-${r.id}`}
                      className="bg-zinc-900 border border-white/10 rounded px-2 py-1 text-xs text-zinc-200 [&>option]:bg-zinc-900">
                      {(data.statuts || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => setOpen(r)} data-testid={`adm-view-${r.id}`} className="text-xs text-amber-400 hover:text-amber-300 mr-3">Détails</button>
                    <button onClick={() => remove(r.id)} className="text-xs text-red-400 hover:text-red-300">Suppr.</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="adm-detail-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(null)} />
          <div className="relative bg-zinc-900 border border-white/10 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-zinc-100">{open.nom}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${LEVEL_TONE[open.niveau_qualification] || ""}`}>{open.niveau_qualification} · {open.score}/100</span>
              </div>
              <button onClick={() => setOpen(null)} className="text-zinc-500 hover:text-zinc-200">✕</button>
            </div>
            <dl className="text-sm space-y-2">
              {[
                ["Contact", `${open.courriel} · ${open.telephone}`], ["Demande pour", open.demande_pour],
                ["Programme d'intérêt", open.programme_interet], ["Programme recommandé", open.programme_recommande],
                ["Objectif", open.objectif_principal], ["Intention", open.intention],
                ["Pourquoi maintenant", open.pourquoi_maintenant], ["Résultat recherché", open.resultat_recherche],
                ["Déjà essayé", open.deja_essaye], ["Prêt à suivre méthode", open.pret_a_suivre_methode],
                ["Fréquence", open.frequence_possible], ["Engagement /10", open.engagement_sur_10],
                ["Décideur final", open.decideur_final], ["Disponibilités", open.disponibilites],
                ["Prêt à investir", open.pret_a_investir], ["Priorité", open.priorite_decision],
                ["Contraintes", open.contraintes],
                ["📣 Source de campagne", open.source_campagne],
              ].filter(([, v]) => v !== "" && v != null).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-2">
                  <dt className="text-zinc-500 text-xs uppercase tracking-wider col-span-1">{k}</dt>
                  <dd className="text-zinc-200 col-span-2">{String(v)}</dd>
                </div>
              ))}
            </dl>
            <label className="block mt-4">
              <span className="text-xs uppercase tracking-widest text-zinc-500 block mb-2">Notes de suivi</span>
              <textarea defaultValue={open.notes_suivi} data-testid="adm-notes"
                onBlur={(e) => e.target.value !== (open.notes_suivi || "") && patch(open.id, { notes_suivi: e.target.value })}
                className="w-full rounded-xl bg-zinc-950 border border-white/10 px-3 py-2 text-sm text-zinc-100 min-h-[70px]" />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
